import useCountryNames from "@/composables/useCountryNames";
import usePolicyObjectMapper from "@/composables/usePolicyObjectMapper";
import useCompanyGroupPolicies from "@/modules/groups/composables/useCompanyGroupPolicies";
import { cloneDeep } from "lodash";
import { MaybeRef, computed, onMounted, onUnmounted, ref, watch } from "vue";

/** El servicio de hotel en el catálogo de servicios: filtra las políticas del grupo. */
export const HOTEL_POLICY_SERVICE = 1;

export interface UseHotelGroupPoliciesProps {
  companyGroupId: MaybeRef<number | undefined>;
  immediate?: boolean;
}

const useHotelGroupPolicies = (props: UseHotelGroupPoliciesProps) => {
  const { immediate } = props;
  const companyGroupId = ref(props.companyGroupId);

  const {
    groupPolicies,
    isLoading: loadingPolicies,
    isFetching: fetchingPolicies,
    resetState: resetPolicies,
    refetch: refetchPolicies,
  } = useCompanyGroupPolicies({
    params: { groupId: companyGroupId, filters: { service: HOTEL_POLICY_SERVICE } },
    immediate,
  });

  const { policyObjectMap } = usePolicyObjectMapper(groupPolicies);

  const countryCodesIds = computed(() => {
    const allowedCountriesPolicy = policyObjectMap.value["allowedCountries"];
    if (!allowedCountriesPolicy) return [];

    return allowedCountriesPolicy.rule[0].options;
  });

  const { countryNames } = useCountryNames({ ids: countryCodesIds, immediate });

  const policyObjectMapWithCountryNames = computed(() => {
    const draft = cloneDeep(policyObjectMap.value);
    const allowedCountriesPolicy = draft["allowedCountries"];
    if (!allowedCountriesPolicy) return draft;

    allowedCountriesPolicy.rule[0].options = countryNames.value as any;
    return draft;
  });

  const availablePolicies = computed(() => {
    return groupPolicies.value.length > 0;
  });

  watch(companyGroupId, async () => {
    resetPolicies();
    await refetchPolicies();
  });

  onMounted(async () => {
    await refetchPolicies();
  });

  onUnmounted(() => {
    resetPolicies();
  });

  return {
    policyObjectMapWithCountryNames,
    policyObjectMap,
    groupPolicies,
    loadingPolicies,
    fetchingPolicies,
    availablePolicies,
    refetchPolicies,
  };
};

export default useHotelGroupPolicies;
